import { isAxiosError } from 'axios'
import http from '@/api/http'
import { bookingTypesPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface BookingType {
  // One row from GET `/booking-types` (cursor-paginated list).

  id: string
  // Booking type id (UUID7).

  name: string | null
  // Display name (e.g. a room or a performer); nullable in the backend.

  description: string | null
  // Optional human-readable description.

  address: string | null
  // Optional free-text address/location.

  performers_ids: string[]
  // Performer person ids linked to this booking type (many-to-many); may be empty.
}

interface BookingTypesResponse extends PageMeta {
  // Paginated list payload from GET `/booking-types`.

  items: BookingType[]
  // Page of booking types.
}

export interface CreateBookingTypePayload {
  // Request body for POST `/booking-types`. All fields optional server-side.

  name?: string | null
  description?: string | null
  address?: string | null
  performers_ids?: string[]
}

export interface UpdateBookingTypePayload {
  // Request body for PATCH `/booking-types/{id}`. `performers_ids: undefined`
  // (omitted) means "don't touch"; the other fields overwrite when present.

  name?: string | null
  description?: string | null
  address?: string | null
  performers_ids?: string[]
}

/**
 * Fetches a page of booking types.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchBookingTypes(cursor?: number): Promise<BookingTypesResponse> {
  const { data } = await http.get<BookingTypesResponse>('/booking-types', {
    params: cursorParams(bookingTypesPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log('[fetchBookingTypes] GET /booking-types response', data)
  }
  return data
}

/**
 * Creates a new booking type via POST `/booking-types`.
 */
export async function createBookingType(payload: CreateBookingTypePayload): Promise<BookingType> {
  const { data } = await http.post<BookingType>('/booking-types', payload)
  if (import.meta.env.DEV) {
    console.log('[createBookingType] POST /booking-types response', data)
  }
  return data
}

/**
 * Updates a booking type via PATCH `/booking-types/{id}`.
 */
export async function updateBookingType(
  id: string,
  payload: UpdateBookingTypePayload,
): Promise<BookingType> {
  const { data } = await http.patch<BookingType>(`/booking-types/${id}`, payload)
  if (import.meta.env.DEV) {
    console.log(`[updateBookingType] PATCH /booking-types/${id} response`, data)
  }
  return data
}

/**
 * Deletes a booking type by id. The backend refuses with HTTP 409 while the
 * type still has bookings (they must be cancelled first); see
 * `isBookingTypeHasBookingsError`.
 */
export async function deleteBookingType(id: string): Promise<void> {
  await http.delete(`/booking-types/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[deleteBookingType] DELETE /booking-types/${id}`)
  }
}

/** True when a failed `deleteBookingType` was refused because bookings remain (HTTP 409). */
export function isBookingTypeHasBookingsError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 409
}
